<template>
  <div class="relationship-card">
    <div class="related-person drawer-top">
      <ProfileCard v-if="profileMemberIdsRef.length == 1"
        :person-id="profileMemberIdsRef[0]"
        show-profile-type
        :dimmed="showDimmed(profileMemberIdsRef[0])">
      </ProfileCard>
      <PersonCard v-else
        :person-id="props.relPersonId"
        layout="small"
        show-profile-type
        show-profile-count="multiple"
        select-mode
        show-restricted
        :dimmed="profileMemberIdsRef.every(showDimmed)"
        @click="onSelected">
      </PersonCard>
    </div>
    <ul v-if="profileMemberIdsRef.length > 1" class="list-unstyled profile-list drawer-stack" :class="{ show: showProfilesRef }">
      <li v-for="id in profileMemberIdsRef" :key="id">
        <ProfileCard :person-id="id" 
          show-profile-type 
          :dimmed="showDimmed(id)"/>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.relationship-card {
  .related-person {
    display: flex;
    align-items: center;

    .person-card, .profile-card {
      width: 400px;
    }
  }

  .profile-list {
    margin-left: 1rem;
  }
}
</style>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import { ProfileUtils } from './ProfileUtils'
import PersonCard from '@/manage/PersonCard.vue'
import ProfileCard from '@/manage/ProfileCard.vue'

const props = defineProps<{
  relPersonId?: string,
  contextPersonId?: string,
  isRelOwner?: boolean,
  ownerOnly?: boolean,
}>()

const viewPersonStore = useViewPersonStore()
const dataGroupStore = useDataGroupStore()

const showProfilesRef = ref(false)

const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.relPersonId))
const contextPersonSetsRef = computed(() => {
  if (!props.contextPersonId || !props.relPersonId)
    return []
  const contextGroupId = CompositeId.getGroupId(props.contextPersonId)
  const personSets = viewPersonStore.getAsyncPersonSets(props.relPersonId, LoadMode.EnsureLoaded)?.data
  return personSets?.filter(ps => ps.groupId === contextGroupId && ps.memberIds.some(id => CompositeId.hasGroupId(id, contextGroupId!))) ?? []
})

const profileMemberIdsRef = computed(() => {
  if (!viewPersonRef.value)
    return []
  const dataGroups = dataGroupStore.getLoadedGroupList(viewPersonRef.value.groupIds)
  return ProfileUtils.getNonPlaceholderIds(viewPersonRef.value.matchIds, dataGroups)
})
const contextProfileMemberIdsRef = computed(() => {
  if (!contextPersonSetsRef.value.length)
    return []
  const allMemberIds = contextPersonSetsRef.value.flatMap(ps => ps.memberIds)
  const groupIds = allMemberIds.map(id => CompositeId.getGroupId(id)!)
  const dataGroups = dataGroupStore.getLoadedGroupList(groupIds)
  return ProfileUtils.getNonPlaceholderIds(allMemberIds, dataGroups)
})

function showDimmed(id: string) {
  return !!props.contextPersonId && !isInContextPersonSet(id)
}

function isInContextPersonSet(profileId: string) {
  return CompositeId.hasSameGroupId(profileId, props.contextPersonId) || // research group case
    props.isRelOwner && contextProfileMemberIdsRef.value.includes(profileId)
}

watch(props, () => {
  showProfilesRef.value = false
})

function onSelected() {
  showProfilesRef.value = !showProfilesRef.value
}

</script>
